import { useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { useColorCopy } from './visit-colors.tsx';
import { Button, TextInput } from '../ui.tsx';
export function VisitColorTags({ id, onChanged }: { id: string; onChanged: () => void }) {
  const w = useColorCopy(),
    session = useSession();
  const queryClient = useQueryClient();
  const scope = JSON.stringify([
    session.company?.id,
    session.currentLocation?.id,
    session.user?.id,
    session.user?.role,
    id,
  ]);
  const currentScope = useRef<string | null>(scope);
  const scopeGeneration = useRef(0);
  if (currentScope.current !== scope) scopeGeneration.current++;
  currentScope.current = scope;
  useEffect(
    () => () => {
      scopeGeneration.current++;
      currentScope.current = null;
    },
    [],
  );
  const queryKey = [
    'visit-color-tags',
    session.company?.id,
    session.currentLocation?.id,
    session.user?.id,
    session.user?.role,
    id,
  ];
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      api.getFresh<{ version: number; tags: string[] }>(`/schedule/appointments/${id}/color-tags`, signal),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const dirty = useRef(false);
  const previousScope = useRef(scope);
  const [tags, setTags] = useState(''),
    [version, setVersion] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  useEffect(() => {
    const switched = previousScope.current !== scope;
    if (switched) {
      dirty.current = false;
      previousScope.current = scope;
    }
    if (!switched && (dirty.current || busy)) return;
    setTags(query.data?.tags.join(', ') ?? '');
    setVersion(query.data?.version ?? 0);
    setError('');
    setBusy(false);
  }, [query.data, scope]);
  if (!session.can('schedule:write') || !['owner', 'manager'].includes(session.user?.role ?? '')) return null;
  async function save() {
    const savingScope = scope;
    const savingGeneration = scopeGeneration.current;
    setBusy(true);
    setError('');
    try {
      const result = await api.put<{ version: number; tags: string[] }>(`/schedule/appointments/${id}/color-tags`, {
        expectedVersion: version,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
      if (currentScope.current !== savingScope || scopeGeneration.current !== savingGeneration) return;
      dirty.current = false;
      queryClient.setQueryData(queryKey, result);
      setVersion(result.version);
      setTags(result.tags.join(', '));
      onChanged();
    } catch (e) {
      if (currentScope.current === savingScope && scopeGeneration.current === savingGeneration)
        setError(e instanceof Error && 'code' in e && e.code === 'STALE_VERSION' ? w.tagsStale : w.tagsFailed);
    } finally {
      if (currentScope.current === savingScope && scopeGeneration.current === savingGeneration) setBusy(false);
    }
  }
  async function reload() {
    const generation = scopeGeneration.current;
    const reloadingScope = scope;
    setBusy(true);
    const result = await query.refetch();
    if (currentScope.current !== reloadingScope || scopeGeneration.current !== generation) return;
    if (result.data && !result.error) {
      dirty.current = false;
      setTags(result.data.tags.join(', '));
      setVersion(result.data.version);
      setError('');
    } else setError(w.tagsLoadFailed);
    setBusy(false);
  }
  return (
    <div className="mb-5 border-b border-[color:var(--color-line)] pb-5" aria-busy={busy || query.isPending}>
      <div className="max-w-xl">
        <TextInput
          label={w.tags}
          hint={w.tagHint}
          error={error || (query.isError ? w.tagsLoadFailed : undefined)}
          tone="internal"
          value={tags}
          disabled={busy || !query.data}
          onChange={(e) => {
            dirty.current = true;
            setTags(e.target.value);
          }}
        />
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={busy || !query.data} onClick={() => void save()}>
            {w.saveTags}
          </Button>
          <Button disabled={busy} onClick={() => void reload()}>
            {w.reloadTags}
          </Button>
        </div>
      </div>
    </div>
  );
}
